/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import {useCurrentGlobalGroupId} from '@ion/applications/src/hooks/useCurrentGlobalGroupId';
import {translate} from '@ion/core/src/locale/locale';
import {
  Devices,
  DevicesFilters,
  GenericObject
} from '@ion/core/src/types/entities';
import {
  VoidEventHandler
} from '@ion/core/src/types/general';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {CancelButton} from '@ion/dls/src/editors/controls/CancelButton';
import {SaveButton} from '@ion/dls/src/editors/controls/SaveButton';
import styles, {getClasses} from '@ion/dls/src/editors/editorStyles';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme, IconSize} from '@ion/dls/src/types/types';
import {BulkSCIFinalView} from './BulkSCIFinalView';
import {BulkSCIImport} from './BulkSCIImport';
import {BulkSCIInfo} from './BulkSCIInfo';
import {BulkSCISettings} from './BulkSCISettings';
import {createModularSearchQueryString} from './ModularSearchUtils';
import {
  DEFAULT_QUERY_STRING_FOR_UPDATE,
  getAllSCIDevices,
  getDeviceIdsList,
  modifiedSettingCheck
} from './SCISettingsUtils';
import {DEFAULT_SMART_SEARCH_QUERY} from './types';

export interface BulkSCIUpdateProps {
  open: boolean;
  onClose?: VoidEventHandler;
  selectedDevices?: Devices;
  isAllRowsSelected?: boolean;
  accountName?: string;
  modularSearchParams?: DevicesFilters;
  onBulkSCIUpdate?: Function;
  loading?: boolean;
  bulkUpload?: boolean;
}

// Possible Steps for Bulk SCI Edit
export enum ActiveStep {
  FIRST = 0,
  SECOND = 1,
  LAST = 2
};

// defines the styles used by bulk upload dialog ...
const styleOverrides = (theme: DLSTheme) => ({
  formDialogTitle : {
    borderBottom : `solid 1px ${dlsColor('grey4')}`
  },
  formTitle : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  formDialogContent : {
    margin                         : 'auto',
    minWidth                       : '1000px',
    minHeight                      : '450px',
    [theme.breakpoints.down('lg')] : {
      width : 'fit-content'
    },
    [theme.breakpoints.down('sm')] : {
      paddingTop : '0px'
    }
  },
  closeButton : {
    position  : 'absolute',
    left      : '19px',
    top       : '19px',
    '&:hover' : {
      cursor : 'pointer'
    }
  },
  fields : {
    paddingLeft : '10px'
  },
  orStyle : {
    padding : '168px 8px 0px 31px'
  },
  formTxtField : {
    padding : '42px 0px 0px 22px',
    width   : '380px'
  },
  uploadContainer : {
    display        : 'flex',
    justifyContent : 'center',
    flexDirection  : 'column',
    alignItems     : 'center',
    marginTop      : '20px',
    padding        : '40px 15px',
    background     : dlsColor('white'),
    border         : `1px dashed ${dlsColor('aaBlue')}`,
    boxSizing      : 'border-box'
  },
  icon : {
    display       : 'inline-block',
    verticalAlign : 'middle',
    paddingTop    : 4,
    marginRight   : 24
  },
  message : {
    ...dlsAlt('typography', 'label2'),
    color         : dlsColor('aaBlue'),
    textTransform : 'upperCase',
    fontWeight    : 'bold',
    width         : '132px',
    margin        : '0 auto',
    textAlign     : 'center'
  },
  results : {
    ...dlsAlt('components', 'uploadResults', 'container'),
    display    : 'flex',
    alignItems : 'center',
    padding    : '10px 10px',
    margin     : '15px 0 0 15px',
    '& > p'    : {
      margin : '10px 0'
    }
  },
  uploadTitle : {
    textTransform : 'uppercase'
  },
  bulkContainer : {
    padding : '10px 0px 0px 15px',
    width   : '360px'
  },
  uploadStatusContainer : {
    marginTop      : '20px',
    display        : 'flex',
    justifyContent : 'space-between',
    width          : '100%'
  },
  linearProgress : {
    maxWidth : '150px'
  },
  hideUploadStatusContainer : {
    display        : 'none',
    marginTop      : '20px',
    justifyContent : 'space-between'
  },
  fileInfoSection : {
    display : 'flex'
  },
  fileIcon : {
    display     : 'flex',
    marginRight : '12px'
  },
  fileSize : {
    opacity : '0.7'
  },
  sciInfoText : {
    paddingTop : '25px',
    fontSize   : '16px'
  },
  chipWrapper : {
    padding : '30px 0px 0px 23px'
  },
  infoIcon : {
    margin        : '0 6px',
    display       : 'inline-block',
    verticalAlign : 'middle',
    cursor        : 'pointer'
  },
  nonSCIText : {
    fontSize : '16px',
    padding  : '25px 0 15px 0'
  },
  disableUploadContainer : {
    display        : 'flex',
    justifyContent : 'center',
    flexDirection  : 'column',
    alignItems     : 'center',
    marginTop      : '20px',
    padding        : '40px 15px',
    background     : dlsColor('white'),
    border         : `1px dashed ${dlsColor('aaBlue')}`,
    boxSizing      : 'border-box',
    pointerEvents  : 'none'
  },
  scitypeInfo : {
    height    : '450px',
    minHeight : '450px',
    overflowY : 'auto'
  },
  sciSubTitleText : {
    fontSize : '12px'
  },
  chipsStyle : {
    height    : '90px',
    minHeight : '61px',
    overflowY : 'auto'
  },
  sciInfoContent : {
    padding   : '20px 38px 0 71px',
    height    : '90%',
    overflowY : 'auto'
  },
  input : {
    padding : '10px'
  }
});

/**
 * Display Bulk SCI Setting Dialog
 */
export const BulkSCIUpdate = ({
  open,
  onClose,
  selectedDevices,
  isAllRowsSelected,
  accountName,
  modularSearchParams,
  onBulkSCIUpdate,
  loading,
  bulkUpload
}: BulkSCIUpdateProps) => {
  const editorStyles = useExtendStyles(styles, styleOverrides);
  const classes      = editorStyles();

  const [activeStep, setActiveStep]                           = useState<number>(ActiveStep.FIRST);
  const [uploadValidation, setUploadValidation]               = useState<boolean>(false);
  const [selectedSCITypesDevices, setSelectedSCITypesDevices] = useState([]);
  const [selectedNonSCIDevices, setSelectedNonSCIDevices]     = useState<GenericObject[]>([]);
  const [selectedSCITypes, setSelectedSCITypes]               = useState<string[]>([]);
  const [selectedSCILength, setSelectedSCILength]             = useState<Number[]>([]);
  const [selectedDevicetype, setSelectedDevicetype]           = useState<string>('');
  const [changedSettingFields, setChangedSettingFields]       = useState({});
  const [isPersist, setIsPersist]                             = useState(false);
  const [bulkUploadPersist, setBulkUploadPersist]             = useState({});
  const [bulkSettingPersist, setBulkSettingPersist]           = useState({});
  const [showSnackBar, setShowSnackBar]                       = useState<boolean>(false);
  const [taskNickName, setTaskNickName]                       = useState<string>('');
  const accountId                                             = useCurrentGlobalGroupId();

  useEffect(() => {
    if (selectedDevices) {
      const allDevices = getAllSCIDevices(selectedDevices);
      const sciDevices = allDevices?.sciDevices;
      setSelectedSCITypesDevices([...sciDevices]);
      setSelectedNonSCIDevices([...allDevices?.nonSciDevices]);

      setSelectedSCITypes([...allDevices?.selectedAllTypes]);
      setSelectedSCILength([...allDevices?.typeDevicesLength]);
      setSelectedDevicetype((sciDevices.length && sciDevices[0].length) ? sciDevices[0][0]?.typeName : '');
    }
  }, [selectedDevices]);

  const onSave = useCallback(() => {
    if (activeStep === ActiveStep.SECOND) {
      modifiedSettingCheck(changedSettingFields)
        ? setActiveStep(activeStep+1)
        : setShowSnackBar(true);
      return;
    } else if (activeStep === ActiveStep.LAST) {
      updateSCISetting();
      return;
    }
    if (selectedDevices?.length) {
      setActiveStep(activeStep+1);
      setIsPersist(true);
    } else {
      setUploadValidation(true);
    }
  }, [activeStep, changedSettingFields, selectedDevices, taskNickName]);

  const onBackClick = useCallback(() => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  }, [activeStep]);

  function closeSnack() {
    setShowSnackBar(false);
  }

  const updateSCISetting = useCallback(() => {
    const commandPayload = {
      accountId,
      devices        : getDevicePayload(),
      deviceCommands : [{
        commandType : 'SciDeviceSettings',
        parameters  : changedSettingFields
      }],
      taskNickName
    };
    onBulkSCIUpdate(commandPayload);
  }, [accountId, changedSettingFields, taskNickName]);

  const getDevicePayload = useCallback(() => {
    const ids = getDeviceIdsList(selectedSCITypesDevices[selectedSCITypes.indexOf(selectedDevicetype)]);
    let devices;
    if (isAllRowsSelected) {
      devices = {
        smartSearch : {
          search : {queryString, accountId}
        }
      };
    }
    devices = {...devices, ids};
    return devices;
  }, [selectedSCITypesDevices, selectedSCITypes, selectedDevicetype]);

  const queryString = useMemo((): string => {
    const searchQuery = createModularSearchQueryString(modularSearchParams);
    const devicequeryString = searchQuery !== DEFAULT_SMART_SEARCH_QUERY
      ? `${DEFAULT_QUERY_STRING_FOR_UPDATE} AND ${searchQuery}`
      : DEFAULT_QUERY_STRING_FOR_UPDATE;
    return devicequeryString;
  }, [modularSearchParams]);

  const isDisabled = useMemo((): boolean => (
    !(selectedSCITypesDevices?.length > 0) && !bulkUpload
  ), [selectedSCITypesDevices, bulkUpload]);

  return (
    <>
      <Dialog open={open}
        classes={getClasses(classes)}>
        <DialogTitle classes={{
          root : classes.formDialogTitle
        }}>
          <div className={classes.formTitle}>
            <div onClick={onClose} className={classes.closeButton}>
              <MaterialExitCloseIcon size={IconSize.medium}
                color={dlsColor('grey3')} />
            </div>
            <Typography component='h1' variant='h2'>
              {translate('admin-devices:view.bulkSciSetting.bulkUpdateLabel')}
            </Typography>
          </div>
        </DialogTitle>
        <DialogContent className={classes.formDialogContent}>
          {activeStep === ActiveStep.FIRST && (
            <>
              {selectedDevices?.length ? (
                <BulkSCIInfo classes={classes}
                  selectedSCITypesDevices={selectedSCITypesDevices}
                  selectedSCITypes={selectedSCITypes}
                  selectedSCILength={selectedSCILength}
                  setSelectedDevicetype={setSelectedDevicetype}
                  selectedDevicetype={selectedDevicetype}
                  selectedNonSCIDevices={selectedNonSCIDevices}
                  setSelectedSCITypesDevices={setSelectedSCITypesDevices}
                  setBulkSettingPersist={setBulkSettingPersist}/>
              ) : (
                <BulkSCIImport classes={classes}
                  uploadValidation={uploadValidation}
                  setActiveStep={setActiveStep}
                  setSelectedSCITypesDevices={setSelectedSCITypesDevices}
                  setUploadValidation={setUploadValidation}
                  isPersist={isPersist}
                  setBulkUploadPersist={setBulkUploadPersist}
                  bulkUploadPersist={bulkUploadPersist}
                  setIsPersist={setIsPersist}
                  setSelectedDevicetype={setSelectedDevicetype}
                  accountID={accountId}
                  setSelectedSCITypes={setSelectedSCITypes}/>
              )}
            </>
          )}
          {activeStep === ActiveStep.SECOND && (
            <BulkSCISettings selectedDevicetype={selectedDevicetype}
              setChangedSettingFields={setChangedSettingFields}
              bulkSettingPersist={bulkSettingPersist}
              setBulkSettingPersist={setBulkSettingPersist}/>
          )}
          {activeStep === ActiveStep.LAST && (
            <BulkSCIFinalView selectedSCITypesDevices={selectedSCITypesDevices}
              changedSettingFields={changedSettingFields}
              selectedDevicetype={selectedDevicetype}
              isAllRowsSelected={isAllRowsSelected}
              accountName={accountName}
              uploadValidation={uploadValidation}
              setSelectedSCITypesDevices={setSelectedSCITypesDevices}
              selectedTypeIndex={selectedSCITypes.indexOf(selectedDevicetype)}
              setChangedSettingFields={setChangedSettingFields}
              setTaskNickName={setTaskNickName}/>
          )}
        </DialogContent>
        <DialogActions className={classes.formActions}>
          <CancelButton onCancel={onClose} />
          {activeStep > ActiveStep.FIRST && (
            <TertiaryButton disabled={false} onClick={onBackClick}>
              {translate('admin-devices:view.bulkSciSetting.back')}
            </TertiaryButton>
          )}
          <div className={classes.primaryButton}>
            <SaveButton onSave={onSave}
              loading={loading}
              disabled={isDisabled}
              label={activeStep === ActiveStep.LAST ? translate('common:featureActions.update')
                : translate('admin-devices:view.bulkSciSetting.next')}/>
          </div>
        </DialogActions>
      </Dialog>
      <IonSnackbar show={showSnackBar}
        message={translate('admin-devices:view.bulkSciSetting.bulkInfoMessages.noSettingsChangedMessage')}
        actionLabel={translate('common:editors.delete.ok')}
        onAction={closeSnack}
        onClose={closeSnack}
        autoHideDuration={3000} />
    </>
  );
};

export default BulkSCIUpdate;
